import { Autocomplete, AutocompleteProps, createTheme, ThemeOptions, ThemeProvider, useTheme } from "@mui/material";
import * as React from "react";
import { ChipTypeMap } from "@mui/material/Chip";
import { useMemo, useState } from "react";
import scrollbarThemeOverrides from "../scrollbar";

const autocompleteThemeOptions = (fontSize: string): ThemeOptions => ({
	components: {
		MuiAutocomplete: {
			defaultProps: {
				size: "small",
				componentsProps: {
					popper: {
						sx: {
							"& .MuiPaper-root": {
								border: "1px solid #919191",
								borderTopLeftRadius: 0,
								borderTopRightRadius: 0,
								borderBottomLeftRadius: 3,
								borderBottomRightRadius: 3,
							},
							"& .MuiAutocomplete-listbox": {
								padding: 0,
								fontSize,
								"& .MuiAutocomplete-option.Mui-focused": {
									boxShadow: "inset 0 0 0 2px var(--focus-outline-color-dark)",
									backgroundColor: "inherit",
								},
								"& .MuiAutocomplete-option:hover": {
									boxShadow: "none",
									backgroundColor: "#ECECEC",
								},
								...scrollbarThemeOverrides,
							},
							"& .MuiAutocomplete-paper": {
								fontSize,
							},
							"& .MuiAutocomplete-option": {
								color: "#4D4D4D",
							},
							"& .MuiAutocomplete-option + .MuiAutocomplete-option": {
								borderTop: "none",
							},
							"& .MuiAutocomplete-option:last-child": {
								borderBottom: "none",
							},
						},
						modifiers: [
							{
								name: "offset",
								options: {
									offset: [0, -2],
								},
							},
						],
					},
					paper: {
						elevation: 0,
					},
				},
			},
		},
	},
});

export default function CoreAutocomplete<
	Value,
	Multiple extends boolean | undefined,
	DisableClearable extends boolean | undefined,
	FreeSolo extends boolean | undefined,
	ChipComponent extends React.ElementType = ChipTypeMap["defaultComponent"],
>(
	autocompleteProps: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent> & {
		fontSize?: string;
	},
) {
	const baseTheme = useTheme();
	const theme = useMemo(
		() => createTheme(baseTheme, autocompleteThemeOptions(autocompleteProps.fontSize ?? "14px")),
		[baseTheme, autocompleteProps.fontSize],
	);

	const [open, setOpen] = useState(false);

	return (
		<ThemeProvider theme={theme}>
			<Autocomplete
				{...autocompleteProps}
				open={open}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				onKeyDown={(e) => {
					if (e.key === "Enter" && !open) {
						e.preventDefault();
						setOpen(true);
					}
				}}
			/>
		</ThemeProvider>
	);
}
